import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Link as RouterLink, useNavigate, useParams } from 'react-router-dom';
import {
  Alert, Box, Button, Chip, CircularProgress, Dialog, DialogActions, DialogContent, DialogTitle, Paper, Table,
  Link, TableBody, TableCell, TableHead, TableRow, TextField, Typography,
} from '@mui/material';
import { BillOfEntryService, CustomsPaymentService } from '../../../services/billOfEntry.service';
import { AccountService } from '../../../services/account.service';
import ConfirmDialog from '../../common/ConfirmDialog';
import { BillOfEntry, CREDIT_SOURCE_LABELS, TREATMENT_LABELS } from '../../../interfaces/procurement/billOfEntry.interface';
import { Account } from '../../../interfaces/accounting.interface';
import { AccountAutocomplete } from '../../common/AccountAutocomplete';
import AttachmentsPanel from '../../common/AttachmentsPanel';
import { leafAccounts } from '../../../utils/postingAccounts';
import { useToast } from '../../../context/ToastContext';
import { usePermission } from '../../../hooks/usePermission';
import { PERMISSIONS } from '../../../constants/permissions';
import { formatMoney, humanizeEnum } from '../../../utils/format';
import { formatDate, toLocalIsoDate } from '../../../utils/dateUtils';
import { BOE_STATUS_COLORS } from './BillOfEntryList';

export const BillOfEntryDetail: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const toast = useToast();
  const canPost = usePermission(PERMISSIONS.GOODS_RECEIPT_POST);
  const [boe, setBoe] = useState<BillOfEntry | null>(null);
  const [busy, setBusy] = useState(false);
  const [confirm, setConfirm] = useState<null | { kind: 'BOE' } | { kind: 'PAYMENT'; paymentId: string }>(null);
  const journalLink = (jeId?: string, label = 'Journal') => jeId
    ? <Link component={RouterLink} to={`/accounting/journal-entries/${jeId}`}>{label}</Link> : null;
  const [payOpen, setPayOpen] = useState(false);
  const [accounts, setAccounts] = useState<Account[]>([]);
  const [payAmount, setPayAmount] = useState('');
  const [payDate, setPayDate] = useState(toLocalIsoDate());
  const [bankId, setBankId] = useState('');
  const [challanNo, setChallanNo] = useState('');
  const [challanDate, setChallanDate] = useState(toLocalIsoDate());

  const load = useCallback(async () => {
    if (!id) return;
    try { setBoe(await BillOfEntryService.getById(id)); }
    catch (e) { toast.showApiError(e, 'Failed to load bill of entry'); }
  }, [id]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => { load(); }, [load]);

  const run = async (fn: () => Promise<unknown>, ok: string, fail: string) => {
    try { setBusy(true); await fn(); toast.showSuccess(ok); await load(); }
    catch (e) { toast.showApiError(e, fail); }
    finally { setBusy(false); }
  };

  const openPay = async () => {
    if (accounts.length === 0) {
      try { setAccounts(await AccountService.getAllAccounts()); }
      catch (e) { toast.showApiError(e, 'Failed to load accounts'); }
    }
    setPayAmount(boe ? String(boe.customsOutstanding) : '');
    setPayOpen(true);
  };

  const submitPayment = async () => {
    if (!boe) return;
    await run(async () => {
      await CustomsPaymentService.create({
        billOfEntryId: boe.id, paymentDate: payDate, amount: Number(payAmount), bankGlAccountId: bankId,
        challanNo: challanNo.trim(), challanDate,
        chargeIds: boe.charges.filter(c => c.creditSource === 'CUSTOMS').map(c => c.id),
      });
      setPayOpen(false);
    }, 'Customs payment recorded', 'Failed to record customs payment');
  };

  const postingAccounts = useMemo(() => leafAccounts(accounts), [accounts]);
  const payValid = Number(payAmount) > 0 && bankId !== '' && challanNo.trim() !== '';

  if (!boe) return <Box sx={{ p: 3 }}><CircularProgress /></Box>;

  return (
    <Box sx={{ p: 3, display: 'flex', flexDirection: 'column', gap: 2 }}>
      <Box sx={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: 1 }}>
        <Typography variant="h5">Bill of Entry {boe.boeNumber}</Typography>
        <Chip label={boe.status} color={BOE_STATUS_COLORS[boe.status]} size="small" />
        <Box sx={{ flex: 1 }} />
        <Button onClick={() => navigate('/procurement/bills-of-entry')}>Back</Button>
        {canPost && boe.status === 'DRAFT' && (<>
          <Button data-help="procurement.boeDetail.edit" onClick={() => navigate(`/procurement/bills-of-entry/${boe.id}/edit`)}>Edit</Button>
          <Button data-help="procurement.boeDetail.delete" color="error" disabled={busy} onClick={() => run(async () => { await BillOfEntryService.remove(boe.id); navigate('/procurement/bills-of-entry'); }, 'Draft deleted', 'Failed to delete draft')}>Delete</Button>
          <Button data-help="procurement.boeDetail.post" variant="contained" disabled={busy} onClick={() => run(() => BillOfEntryService.post(boe.id), 'Bill of entry posted', 'Failed to post bill of entry')}>Post</Button>
        </>)}
        {canPost && boe.status === 'POSTED' && (<>
          <Button data-help="procurement.boeDetail.pay" variant="outlined" disabled={busy || boe.customsOutstanding <= 0} onClick={openPay}>Record customs payment</Button>
          <Button data-help="procurement.boeDetail.reverse" color="error" disabled={busy} onClick={() => setConfirm({ kind: 'BOE' })}>Reverse</Button>
        </>)}
      </Box>

      <Paper sx={{ p: 2, display: 'flex', flexWrap: 'wrap', gap: 3 }} data-help-section="procurement.boeDetail.summary">
        <Typography>Date: {formatDate(boe.boeDate)}</Typography>
        <Typography>Customs house: {boe.customsHouse ?? '—'}</Typography>
        <Typography>Allocation: {humanizeEnum(boe.allocationMethod)}</Typography>
        <Typography>Capitalised: {formatMoney(boe.capitalisedTotal)}</Typography>
        <Typography>VAT / AT credit: {formatMoney(boe.inputTaxTotal)}</Typography>
        <Typography>AIT: {formatMoney(boe.aitTotal)}</Typography>
        {boe.journalEntryId && <Typography>Posting: {journalLink(boe.journalEntryId, 'View journal entry')}</Typography>}
        {boe.reversalJournalEntryId && <Typography>Reversal: {journalLink(boe.reversalJournalEntryId, 'View reversal journal')}</Typography>}
        <Typography>Customs payable: {formatMoney(boe.customsPayable)} (paid {formatMoney(boe.customsPaid)}, outstanding {formatMoney(boe.customsOutstanding)})</Typography>
      </Paper>
      {boe.notes && <Alert severity="info">{boe.notes}</Alert>}

      <Typography variant="h6">Lines</Typography>
      <Paper sx={{ overflowX: 'auto' }} data-help-section="procurement.boeDetail.lines">
        <Table size="small">
          <TableHead><TableRow><TableCell data-help="procurement.boeDetail.line.grn">GRN</TableCell><TableCell data-help="procurement.boeDetail.line.item">Item</TableCell><TableCell data-help="procurement.boeDetail.line.qty" align="right">Qty</TableCell>
            <TableCell data-help="procurement.boeDetail.line.value" align="right">Value</TableCell><TableCell data-help="procurement.boeDetail.line.alloc" align="right">Allocated</TableCell>
            <TableCell data-help="procurement.boeDetail.line.stock" align="right">To stock</TableCell><TableCell data-help="procurement.boeDetail.line.expensed" align="right">Expensed</TableCell></TableRow></TableHead>
          <TableBody>
            {boe.lines.map(l => (
              <TableRow key={l.id}>
                <TableCell>{l.grnNumber}</TableCell><TableCell>{l.itemName}</TableCell>
                <TableCell align="right">{l.receivedQty}</TableCell><TableCell align="right">{formatMoney(l.lineValue)}</TableCell>
                <TableCell align="right">{formatMoney(l.allocatedAmount)}</TableCell>
                <TableCell align="right">{formatMoney(l.onHandAmount)}</TableCell>
                <TableCell align="right">{formatMoney(l.consumedAmount)}</TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </Paper>

      <Typography variant="h6">Charges</Typography>
      <Paper sx={{ overflowX: 'auto' }} data-help-section="procurement.boeDetail.charges">
        <Table size="small">
          <TableHead><TableRow><TableCell data-help="procurement.boeDetail.chg.kind">Kind</TableCell><TableCell data-help="procurement.boeDetail.chg.treatment">Treatment</TableCell><TableCell data-help="procurement.boeDetail.chg.via">Via</TableCell>
            <TableCell data-help="procurement.boeDetail.chg.amount" align="right">Amount</TableCell><TableCell data-help="procurement.boeDetail.chg.ref">Reference</TableCell></TableRow></TableHead>
          <TableBody>
            {boe.charges.map(c => (
              <TableRow key={c.id}>
                <TableCell>{c.kind}</TableCell><TableCell>{TREATMENT_LABELS[c.treatment] ?? humanizeEnum(c.treatment)}</TableCell>
                <TableCell>{CREDIT_SOURCE_LABELS[c.creditSource] ?? humanizeEnum(c.creditSource)}{c.supplierBillNumber ? ` (${c.supplierBillNumber})` : ''}</TableCell>
                <TableCell align="right">{formatMoney(c.amount)}</TableCell><TableCell>{c.description ?? c.reference ?? ''}</TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </Paper>

      <Typography variant="h6">Customs payments</Typography>
      {boe.payments.length === 0 ? <Typography color="text.secondary">No customs payments yet.</Typography> : (
        <Paper sx={{ overflowX: 'auto' }} data-help-section="procurement.boeDetail.payments">
          <Table size="small">
            <TableHead><TableRow><TableCell data-help="procurement.boeDetail.pay.date">Date</TableCell><TableCell data-help="procurement.boeDetail.pay.challan">Challan</TableCell><TableCell data-help="procurement.boeDetail.pay.challanDate">Challan date</TableCell>
              <TableCell data-help="procurement.boeDetail.pay.amount" align="right">Amount</TableCell><TableCell data-help="procurement.boeDetail.pay.journal">Journal</TableCell><TableCell data-help="procurement.boeDetail.pay.actions" /></TableRow></TableHead>
            <TableBody>
              {boe.payments.map(p => (
                <TableRow key={p.id}>
                  <TableCell>{formatDate(p.paymentDate)}</TableCell><TableCell>{p.challanNo}</TableCell>
                  <TableCell>{formatDate(p.challanDate)}</TableCell><TableCell align="right">{formatMoney(p.amount)}</TableCell>
                  <TableCell>
                    {journalLink(p.journalEntryId)}
                    {p.reversalJournalEntryId && <> {journalLink(p.reversalJournalEntryId, 'Reversal')}</>}
                  </TableCell>
                  <TableCell>
                    {p.reversedAt ? <Chip size="small" color="error" label="Reversed" />
                      : canPost && <Button data-help="procurement.boeDetail.pay.reverse" size="small" color="error" disabled={busy}
                        onClick={() => setConfirm({ kind: 'PAYMENT', paymentId: p.id })}>Reverse</Button>}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </Paper>
      )}

      <AttachmentsPanel entityType="BILL_OF_ENTRY" entityId={boe.id} canManage={canPost} />

      <ConfirmDialog
        open={confirm !== null}
        title={confirm?.kind === 'PAYMENT' ? 'Reverse customs payment?' : 'Reverse bill of entry?'}
        message={confirm?.kind === 'PAYMENT'
          ? 'This posts a reversing journal and reopens the customs amount as outstanding.'
          : 'This posts a reversing journal and takes the landed cost back out of stock. Customs payments must be reversed first.'}
        confirmLabel="Reverse"
        confirmColor="error"
        submitting={busy}
        onCancel={() => { if (!busy) setConfirm(null); }}
        onConfirm={async () => {
          const c = confirm;
          if (!c || busy) return;
          if (c.kind === 'BOE') await run(() => BillOfEntryService.reverse(boe.id), 'Bill of entry reversed', 'Failed to reverse bill of entry');
          else await run(() => CustomsPaymentService.reverse(c.paymentId), 'Payment reversed', 'Failed to reverse payment');
          setConfirm(null);
        }}
      />

      <Dialog open={payOpen} onClose={() => setPayOpen(false)} fullWidth maxWidth="sm" data-help-page="procurement.boePayForm.page">
        <DialogTitle>Record customs payment</DialogTitle>
        <DialogContent sx={{ display: 'flex', flexDirection: 'column', gap: 2, pt: 2 }}>
          <TextField data-help="procurement.boePayForm.field.amount" label="Amount" type="number" value={payAmount} onChange={e => setPayAmount(e.target.value)} fullWidth sx={{ mt: 1 }} />
          <TextField data-help="procurement.boePayForm.field.date" label="Payment date" type="date" value={payDate} onChange={e => setPayDate(e.target.value)} InputLabelProps={{ shrink: true }} fullWidth />
          <AccountAutocomplete data-help="procurement.boePayForm.field.account" accounts={postingAccounts} value={bankId} onChange={setBankId} label="Paid from (bank / cash)" required />
          <TextField data-help="procurement.boePayForm.field.challan" label="Challan number" required value={challanNo} onChange={e => setChallanNo(e.target.value)} fullWidth />
          <TextField data-help="procurement.boePayForm.field.challanDate" label="Challan date" type="date" value={challanDate} onChange={e => setChallanDate(e.target.value)} InputLabelProps={{ shrink: true }} fullWidth />
        </DialogContent>
        <DialogActions>
          <Button data-help="procurement.boePayForm.cancel" onClick={() => setPayOpen(false)}>Cancel</Button>
          <Button data-help="procurement.boeDetail.payConfirm" variant="contained" disabled={busy || !payValid} onClick={submitPayment}>Record payment</Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default BillOfEntryDetail;
